import IconCloneSolid from '@/assets/icons/font_awesome/clone-solid.svg?react';
import { UI_PRIMITIVE__button_with_icon_animated } from '@/components/ui/UI_PRIMITIVE__button_with_icon_animated';
import { UI_PRIMITIVE__tooltip } from '@/components/ui/UI_PRIMITIVE__tooltip';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_slice_generation, use_slice_workspace } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import i18n_page_user_tasks from '@/locales/keys/i18n_page_user_tasks.json';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_user_task_duplicate } from '@/state/generation/user_task/entity/remote_user_task_duplicate';
import { set_current_generation } from '@/utils/utils_client_workspace_child_entity_viewers';
import { md } from '@/utils/utils_html';
import { assert_array_not_empty } from '@shared/asserts/utils_asserts';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { DTO__user_task } from '@shared/dto/user_task/DTO__user_task';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  item: DTO__user_task;
  isSelected?: boolean;
}

export function U_BUTTON__user_task_duplicate({ item, isSelected = false }: Props) {
  const dispatch = use_dispatch();

  const { t } = useTranslation([ENUM__i18n_namespace.PAGE_USER_TASKS, ENUM__i18n_namespace.BASE_UI]);

  const { toast } = useToast();

  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);

  const { user_tasks_map } = use_slice_generation(['user_tasks_map']);

  const [component_id, set_component_id] = useState('');
  const notifier = HOOK__notifier(component_id, ENUM__workspace_entity.ENTITY_USER_TASK);

  const [is_loading, set_is_loading] = useState(false);
  const [user_task_new_id, set_user_task_new_id] = useState('');

  const tooltip_title = t(i18n_page_user_tasks.button.task_dublicate.title);
  /*
   * EFFECTS
   */

  use_effect(() => {
    if (!user_task_new_id) return;

    let user_task = user_tasks_map.get_or_assert(user_task_new_id, { context: 'user_task' });
    assert_array_not_empty(user_task.generations, { context: 'user_task.generations' });

    set_user_task_new_id('');

    let generation_id = user_task.generations[0]._id;
    void set_current_generation(generation_id);
  }, [user_task_new_id]);

  use_effect(() => {
    if (component_id) void notifier.send_action();
  }, [component_id]);

  /*
   * EVENTS
   */
  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();

    set_is_loading(true);
    let action_result = await dispatch(remote_user_task_duplicate(item._id));
    set_is_loading(false);

    if (typeof action_result.payload != 'string') return;

    let user_task_id = action_result.payload;

    set_user_task_new_id(user_task_id);

    set_component_id(`${ENUM__component_type.PAGE_USER_TASKS_LIST_ITEM}_${user_task_id}`);

    toast({ title: t(i18n_base_ui.toasts.user_task_duplicate.title) });
  }

  /*
   * HTML
   */
  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_USER_TASK_CREATE)) return <></>;

  return (
    <UI_PRIMITIVE__tooltip {...{ tooltip_title }} side="top">
      <UI_PRIMITIVE__button_with_icon_animated
        className="w-7 p-0"
        size="square-5"
        variant="no-border-on-component"
        {...{ onClick }}
        disabled={is_loading}
        iconClassNameBase={`!w-3.5 !h-3.5 ${md(`${isSelected ? '!text-bar' : '!text-component-item-foreground '}`, ``)}`}
        iconClassNameHovered={'!text-bar-foreground'}
        iconClassNameNonHovered={`!text-bar `}
        Icon={IconCloneSolid}
      />
    </UI_PRIMITIVE__tooltip>
  );
}
